import React, { useState, useEffect } from 'react';
import { FiHeadphones, FiPlus, FiCalendar, FiClock, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import soporteService from '../services/soporteService';
import Modal from './Modal';
import './SoporteBooking.css';

import Loading from './Loading';
const SoporteBooking = () => {
  const [slots, setSlots] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Estados del formulario
  const [step, setStep] = useState(1); // 1: selección, 2: formulario, 3: confirmación
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
  });
  const [createdReservation, setCreatedReservation] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    loadReservations();
  }, []);

  const loadSlots = async () => {
    try {
      setLoading(true);
      const response = await soporteService.getSlots(null, 14);
      setSlots(response.data.slots || []);
    } catch (err) {
      console.error('Error al cargar horarios:', err);
      toast.error('Error al cargar los horarios disponibles');
    } finally {
      setLoading(false);
    }
  };

  const loadReservations = async () => {
    try {
      const response = await soporteService.getReservations();
      setReservations(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error al cargar reservas:', err);
      toast.error('Error al cargar las reservas');
    }
  };

  const handleSlotSelect = (slot) => {
    if (!slot.available) return;
    setSelectedSlot(slot);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSlot) {
      toast.error('Por favor selecciona un horario');
      return;
    }

    try {
      setLoading(true);

      const data = {
        start_time: selectedSlot.start,
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        company: formData.company || undefined,
        notes: formData.notes || undefined,
      };

      const response = await soporteService.createReservation(data);

      setCreatedReservation(response.data.reservation);
      toast.success('¡Reserva creada exitosamente!');
      setStep(3);

      // Recargar datos
      loadReservations();

    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error al crear la reserva';
      toast.error(errorMsg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelReservation = async (id, name) => {
    if (!window.confirm(`¿Estás seguro de que quieres cancelar la reserva de ${name}?`)) {
      return;
    }

    try {
      await soporteService.cancelReservation(id, 'Cancelado por el usuario');
      toast.success('Reserva cancelada exitosamente');
      loadReservations();
    } catch (err) {
      toast.error('Error al cancelar la reserva');
      console.error(err);
    }
  };

  const resetModal = () => {
    setStep(1);
    setSelectedSlot(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      notes: '',
    });
    setCreatedReservation(null);
  };

  const openModal = () => {
    resetModal();
    loadSlots();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetModal();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-CL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const formatted = date.toLocaleDateString('es-CL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: '#f59e0b', label: 'Pendiente' },
      confirmed: { color: '#10b981', label: 'Confirmada' },
      completed: { color: '#3b82f6', label: 'Completada' },
      cancelled: { color: '#ef4444', label: 'Cancelada' },
      no_show: { color: '#6b7280', label: 'No asistió' },
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className="status-badge" style={{ backgroundColor: badge.color }}>
        {badge.label}
      </span>
    );
  };

  const goToNextStep = () => {
    if (selectedSlot) {
      setStep(2);
    }
  };

  // Calcular estadísticas
  const totalReservations = reservations.length;
  const upcomingReservations = reservations.filter(r => r.is_upcoming && r.status === 'confirmed').length;
  const completedReservations = reservations.filter(r => r.status === 'completed').length;
  const cancelledReservations = reservations.filter(r => r.status === 'cancelled').length;

  return (
    <div className="soporte-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <FiHeadphones size={32} className="page-icon" />
          <div>
            <h1>Soporte</h1>
            <p className="page-subtitle">Gestión de sesiones de soporte y asistencia técnica</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={openModal}>
            <FiPlus size={18} />
            Agendar Sesión
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      {reservations.length > 0 && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#e0f2fe' }}>
              <FiCalendar color="#0284c7" size={24} />
            </div>
            <div>
              <div className="stat-value">{totalReservations}</div>
              <div className="stat-label">Total Reservas</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#dcfce7' }}>
              <FiClock color="#16a34a" size={24} />
            </div>
            <div>
              <div className="stat-value">{upcomingReservations}</div>
              <div className="stat-label">Próximas Sesiones</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#dbeafe' }}>
              <FiCheckCircle color="#2563eb" size={24} />
            </div>
            <div>
              <div className="stat-value">{completedReservations}</div>
              <div className="stat-label">Completadas</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: '#fee2e2' }}>
              <FiXCircle color="#dc2626" size={24} />
            </div>
            <div>
              <div className="stat-value">{cancelledReservations}</div>
              <div className="stat-label">Canceladas</div>
            </div>
          </div>
        </div>
      )}

      {/* Reservations Grid */}
      {loading && reservations.length === 0 ? (
        <div className="loading-state">
          <Loading fullScreen={false} size="sm" mensaje="Cargando reservas" />
        </div>
      ) : reservations.length === 0 ? (
        <div className="empty-state">
          <FiHeadphones size={64} color="#d1d5db" />
          <h3>No tienes reservas de soporte</h3>
          <p>Agenda tu primera sesión de soporte técnico</p>
          <button className="btn-primary" onClick={openModal}>
            <FiPlus size={18} />
            Agendar Sesión
          </button>
        </div>
      ) : (
        <div className="reservations-grid">
          {reservations.map((reservation) => (
            <div key={reservation.id} className="reservation-card">
              <div className="reservation-header">
                <div className="reservation-info">
                  <h3>{reservation.name}</h3>
                  <p className="reservation-email">{reservation.email}</p>
                  {reservation.company && (
                    <p className="reservation-company">{reservation.company}</p>
                  )}
                </div>
                {getStatusBadge(reservation.status)}
              </div>

              <div className="reservation-details">
                <div className="detail-item">
                  <FiCalendar size={16} color="#6b7280" />
                  <span>{formatDate(reservation.scheduled_date)}</span>
                </div>
                {reservation.phone && (
                  <div className="detail-item">
                    <span className="detail-label">📱 {reservation.phone}</span>
                  </div>
                )}
                {reservation.notes && (
                  <div className="detail-item">
                    <span className="detail-label">📝 {reservation.notes}</span>
                  </div>
                )}
              </div>

              {reservation.google_meet_link && reservation.status === 'confirmed' && (
                <div className="reservation-actions">
                  <a
                    href={reservation.google_meet_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-success"
                  >
                    📹 Unirse a Meet
                  </a>

                  {reservation.is_upcoming && (
                    <button
                      className="btn-danger-outline"
                      onClick={() => handleCancelReservation(reservation.id, reservation.name)}
                    >
                      Cancelar
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* MODAL */}
      <Modal
        isOpen={showModal}
        onClose={closeModal}
        title="Agenda tu Sesión de Soporte"
        subtitle={step === 1 ? "Selecciona un horario disponible" : step === 2 ? "Completa tus datos" : ""}
        icon="🎧"
        size="xl"
        footer={
          step === 1 ? (
            <button
              type="button"
              className="btn-primary"
              onClick={goToNextStep}
              disabled={!selectedSlot}
            >
              Continuar →
            </button>
          ) : step === 2 ? (
            <>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setStep(1)}
              >
                ← Volver
              </button>
              <button
                type="submit"
                form="soporteForm"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Confirmando...' : 'Confirmar Reserva'}
              </button>
            </>
          ) : null
        }
        footerAlignment={step === 2 ? "between" : "end"}
      >
        {/* Step 1: Selección de horario */}
        {step === 1 && (
          <div className="soporte-step">
            {loading && (
              <div className="text-center py-5">
                <Loading fullScreen={false} size="sm" mensaje="Cargando horarios disponibles" />
              </div>
            )}

            {!loading && slots.length > 0 && (
              <div className="slots-container">
                {slots.slice(0, 7).map((day, dayIndex) => (
                  <div key={dayIndex} className="slot-card">
                    <h6 className="slot-card-title">
                      {formatDateShort(day.date)}
                    </h6>
                    <div className="d-flex flex-wrap">
                      {day.slots.map((slot, slotIndex) => (
                        <button
                          key={slotIndex}
                          type="button"
                          className={`slot-button ${selectedSlot?.start === slot.start ? 'selected' : ''}`}
                          onClick={() => handleSlotSelect(slot)}
                          disabled={!slot.available}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Formulario */}
        {step === 2 && (
          <div className="soporte-step">
            <div className="alert alert-info mb-3">
              <strong>Horario seleccionado:</strong> {formatDate(selectedSlot.start)}
            </div>

            <form id="soporteForm" onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Nombre completo *</label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email *</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+56 9 1234 5678"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Empresa</label>
                  <input
                    type="text"
                    className="form-control"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="form-label">¿Algo que quieras que sepamos?</label>
                  <textarea
                    className="form-control"
                    name="notes"
                    rows="3"
                    value={formData.notes}
                    onChange={handleInputChange}
                  ></textarea>
                </div>
              </div>
            </form>
          </div>
        )}

        {/* Step 3: Confirmación */}
        {step === 3 && createdReservation && (
          <div className="soporte-step text-center py-4">
            <div className="mb-4">
              <div className="success-checkmark">✓</div>
            </div>

            <h4 className="confirmation-title">¡Sesión de Soporte Agendada!</h4>
            <p className="mt-3">Hemos enviado la confirmación a tu email con el link de Google Meet.</p>

            <div className="alert alert-success mt-4">
              <p className="mb-2">
                <strong>Horario:</strong> {formatDate(createdReservation.scheduled_date)}
              </p>
              <p className="mb-0">
                <strong>Email:</strong> {createdReservation.email}
              </p>
            </div>

            {createdReservation.google_meet_link && (
              <div className="mb-3">
                <a
                  href={createdReservation.google_meet_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                  style={{ display: 'inline-block', padding: '0.75rem 1.5rem', textDecoration: 'none' }}
                >
                  📹 Abrir Google Meet
                </a>
              </div>
            )}

            <button
              className="btn-primary mt-3"
              onClick={closeModal}
            >
              Cerrar
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SoporteBooking;
